/**
 * Artikel bearbeiten — Inventar 2.0, S7b.
 *
 * The last legacy dialog surface on the detail page. Moved out of the page onto
 * the shared chrome, PRESENTATION ONLY: the eleven controls keep their order,
 * their types and their attributes, the option lists are still built by the
 * page (so the "unknown value is prepended, never silently dropped" rule stays
 * exactly where it was), and the payload, the guards, the URL validation, the
 * quantity clamp and the null-vs-empty coercion all remain in the page's
 * bodyFromForm / submitEdit.
 *
 * STATUS STAYS READ-ONLY. The article status is derived from assignments and
 * from the damage workflow, never typed by a human — so this dialog shows a
 * sentence, not a control, and the "(automatisch)" suffix explains why. The
 * suffix rule itself is unchanged: repair and disposed are the two states a
 * human path can set, so they lose the hint.
 *
 * The block keeps its own <div> rather than a <label>: it labels no control,
 * and the two suites match the sentence by its own text, which must stay the
 * paragraph's only content.
 */
import React from "react";

import { inventoryStatusLabelDe } from "../../../utils/inventoryExport";
import {
  DIALOG_BTN_PRIMARY,
  DIALOG_BTN_SECONDARY,
  DIALOG_CONTROL,
  DialogActions,
  DialogError,
  DialogField,
  InventoryDialog,
} from "./InventoryDialog";

/** Moved verbatim: repair and disposed are human-settable, the rest are derived. */
function statusReadOnlySuffix(status) {
  const s = String(status || "").toLowerCase();
  if (s === "repair" || s === "disposed") return "";
  return " (automatisch)";
}

const GROUP_LABEL =
  "m-0 text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground";

export default function InventoryItemEditDialog({
  item,
  form,
  onChangeForm,
  categoryOptions,
  conditionOptions,
  error,
  saving,
  onSubmit,
  onCancel,
}) {
  return (
    <InventoryDialog title="Artikel bearbeiten" maxWidthClassName="max-w-lg">
      {error ? <DialogError>{error}</DialogError> : null}
      <form onSubmit={onSubmit} className="mt-4 grid grid-cols-1 gap-3">
        <DialogField label="Name *">
          <input
            required
            value={form.name}
            onChange={(e) => onChangeForm((f) => ({ ...f, name: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <div className="grid grid-cols-2 gap-3">
          <DialogField label="Gesamtmenge *">
            <input
              type="number"
              min={1}
              value={form.total_quantity}
              onChange={(e) => onChangeForm((f) => ({ ...f, total_quantity: e.target.value }))}
              className={DIALOG_CONTROL}
            />
          </DialogField>
          <div className="block text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
            Status
            <p className="m-0 mt-1 rounded-[8px] border border-dashed border-border bg-muted px-3 py-2 text-[13px] font-medium normal-case tracking-normal text-foreground">
              {item
                ? `Status: ${inventoryStatusLabelDe(item.status)}${statusReadOnlySuffix(item.status)}`
                : "—"}
            </p>
          </div>
        </div>

        <DialogField label="Kategorie">
          <select
            value={form.category}
            onChange={(e) => onChangeForm((f) => ({ ...f, category: e.target.value }))}
            className={DIALOG_CONTROL}
          >
            <option value="">—</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </DialogField>

        <DialogField label="Marke">
          <input
            value={form.brand}
            onChange={(e) => onChangeForm((f) => ({ ...f, brand: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogField label="Zustand">
          <select
            value={form.condition}
            onChange={(e) => onChangeForm((f) => ({ ...f, condition: e.target.value }))}
            className={DIALOG_CONTROL}
          >
            <option value="">—</option>
            {conditionOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </DialogField>

        <div className="grid grid-cols-2 gap-3">
          <DialogField label="Anschaffung CHF">
            <input
              type="number"
              step="0.01"
              value={form.purchase_price_chf}
              onChange={(e) => onChangeForm((f) => ({ ...f, purchase_price_chf: e.target.value }))}
              className={DIALOG_CONTROL}
            />
          </DialogField>
          <DialogField label="Kaufdatum">
            <input
              type="date"
              value={form.purchase_date}
              onChange={(e) => onChangeForm((f) => ({ ...f, purchase_date: e.target.value }))}
              className={DIALOG_CONTROL}
            />
          </DialogField>
        </div>

        <div className="rounded-[8px] border border-border bg-muted p-3">
          <p className={GROUP_LABEL}>Purchase / Supplier Info</p>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <DialogField label="Lieferanten-Artikelnr.">
              <input
                value={form.supplier_article_number}
                onChange={(e) =>
                  onChangeForm((f) => ({ ...f, supplier_article_number: e.target.value }))
                }
                className={`${DIALOG_CONTROL} bg-card`}
              />
            </DialogField>
            <DialogField label="Gekauft bei">
              <input
                value={form.purchased_from}
                onChange={(e) => onChangeForm((f) => ({ ...f, purchased_from: e.target.value }))}
                className={`${DIALOG_CONTROL} bg-card`}
              />
            </DialogField>
            <DialogField label="Produkt-URL">
              <input
                type="url"
                value={form.product_url}
                onChange={(e) => onChangeForm((f) => ({ ...f, product_url: e.target.value }))}
                className={`${DIALOG_CONTROL} bg-card`}
              />
            </DialogField>
          </div>
        </div>

        <DialogField label="Notizen">
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => onChangeForm((f) => ({ ...f, notes: e.target.value }))}
            className={`${DIALOG_CONTROL} resize-y`}
          />
        </DialogField>

        <DialogActions>
          <button type="button" onClick={onCancel} className={DIALOG_BTN_SECONDARY}>
            Abbrechen
          </button>
          <button type="submit" disabled={saving} className={DIALOG_BTN_PRIMARY}>
            {saving ? "…" : "Speichern"}
          </button>
        </DialogActions>
      </form>
    </InventoryDialog>
  );
}
